import { useState, useEffect } from 'react';
import Actions from '../../../store/Actions';
import { TemplateSelectorState } from './types';
import { showSnackbar } from '../../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../../context/Snackbar/Types';
import { ConfigurationTemplate } from '../Configuration/types';

export const useTemplateSelector = (
  initialTemplateId: string | null | undefined,
  onApplySuccess?: () => void,
  sessionId?: string | null,
  onTemplateApplied?: (templateId: string) => void
): TemplateSelectorState => {
  const [templates, setTemplates] = useState<ConfigurationTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    initialTemplateId ?? null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Update selection when initialTemplateId changes (new file loaded)
  useEffect(() => {
    if (initialTemplateId !== undefined) {
      setSelectedTemplateId(initialTemplateId);
    }
  }, [initialTemplateId]);

  const loadTemplates = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await Actions.listConfigurationTemplates();
      setTemplates(response);
    } catch (err) {
      const errorMessage = 'Failed to load templates';
      setError(errorMessage);
      showSnackbar({
        title: 'Error',
        message: errorMessage,
        type: SnackbarType.error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Silent refresh - doesn't show loading spinner so dropdown can open normally
  const refreshTemplates = async () => {
    try {
      const response = await Actions.listConfigurationTemplates();
      setTemplates(response);
    } catch (err) {
      console.error('Failed to refresh templates:', err);
    }
  };

  const applyRules = async (preserveEditedValues?: boolean): Promise<void> => {
    if (!selectedTemplateId) {
      showSnackbar({
        title: 'No Template Selected',
        message: 'Please select a configuration template',
        type: SnackbarType.warning,
      });
      return;
    }

    setIsApplying(true);
    setError(null);

    try {
      const response = await Actions.applyTemplateRules(
        selectedTemplateId,
        sessionId,
        preserveEditedValues
      );

      // Update store with applied template ID
      if (onTemplateApplied && response.template_id) {
        onTemplateApplied(response.template_id);
      }

      showSnackbar({
        title: 'Rules Applied Successfully',
        message:
          response.message ||
          'Configuration rules have been applied to your data',
        type: SnackbarType.success,
      });

      // Call success callback to refresh data
      if (onApplySuccess) {
        onApplySuccess();
      }
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to apply rules';
      setError(errorMessage);
      showSnackbar({
        title: 'Error Applying Rules',
        message: errorMessage,
        type: SnackbarType.error,
      });
    } finally {
      setIsApplying(false);
    }
  };

  return {
    templates,
    selectedTemplateId,
    isLoading,
    isApplying,
    error,
    setSelectedTemplateId,
    applyRules,
    refreshTemplates,
  };
};
